import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrentUser } from '../contexts/UserContext';
import { useApiClient } from '../api/client';
import { Header } from '../components/Header';

interface AdminStats {
  total_users: number;
  total_campaigns: number;
  admin_users: number;
}

export function Admin() {
  const { user: currentUser } = useCurrentUser();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!currentUser || currentUser.role !== 'admin') {
      navigate('/campaigns');
      return;
    }

    const fetchStats = async () => {
      try {
        setLoading(true);
        const data = await apiClient.get('/users/admin/stats');
        setStats(data as AdminStats);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [currentUser, navigate]);

  if (!currentUser || currentUser.role !== 'admin') {
    return null;
  }

  return (
    <>
      <Header />
      <div style={{ padding: 'var(--space-6)' }}>
        <h1 style={{ fontSize: 'var(--text-2xl)', fontWeight: 700, marginBottom: 'var(--space-6)' }}>
          Admin Dashboard
        </h1>

      {loading && (
        <div style={{ color: 'var(--color-text-secondary)' }}>
          Loading...
        </div>
      )}

      {error && (
        <div style={{
          color: 'var(--color-error)',
          padding: 'var(--space-4)',
          backgroundColor: 'var(--color-error-light)',
          borderRadius: 'var(--radius-md)',
          marginBottom: 'var(--space-6)',
        }}>
          Error: {error}
        </div>
      )}

      {stats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: 'var(--space-6)',
        }}>
          <StatCard label="Total Users" value={stats.total_users} />
          <StatCard label="Total Campaigns" value={stats.total_campaigns} />
          <StatCard label="Admin Users" value={stats.admin_users} />
        </div>
      )}
      </div>
    </>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div
      style={{
        padding: 'var(--space-6)',
        backgroundColor: 'var(--color-surface-2)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--color-border)',
      }}
    >
      <div style={{
        fontSize: 'var(--text-sm)',
        color: 'var(--color-text-secondary)',
        marginBottom: 'var(--space-2)',
      }}>
        {label}
      </div>
      <div style={{
        fontSize: 'var(--text-3xl)',
        fontWeight: 700,
        color: 'var(--color-primary)',
      }}>
        {value}
      </div>
    </div>
  );
}
